import { Youtube, Instagram, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
const logo = "/logo.webp";

export const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center gap-3 mb-4">
              <img src={logo} alt="Valorform logo — science-based personal training in Munich" width={40} height={40} className="h-10 w-10 rounded-lg" loading="lazy" decoding="async" />
              <span className="text-xl font-bold text-foreground">VALORFORM</span>
            </div>
            <p className="text-sm text-muted-foreground mb-2 text-center md:text-left">
              {t('footer.tagline')}
            </p>
            <p className="text-sm text-muted-foreground text-center md:text-left">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-lg font-bold text-foreground mb-4">{t('footer.quickLinks')}</h3>
            <nav aria-label={t('footer.quickLinks')} className="flex flex-col gap-2 text-sm">
              <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.home')}</Link>
              <Link to="/areas-of-expertise" className="text-muted-foreground hover:text-primary transition-colors">{t('nav.whatAreWeDoing')}</Link>
              <Link to="/categories/normal-people" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.fitnessEnthusiastPlans')}</Link>
              <Link to="/categories/elite-athletes" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.eliteAthletePlans')}</Link>
              <Link to="/reviews" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.caseStudy')}</Link>
              <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">{t('blog.heading')}</Link>
              <Link to="/tools" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.toolsHeading')}</Link>
            </nav>
          </div>

          {/* Services (SEO internal-link block) */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-lg font-bold text-foreground mb-4">{t('footer.services')}</h3>
            <nav aria-label={t('footer.services')} className="flex flex-col gap-2 text-sm">
              <Link to="/services/gym-training" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.footerServices.personalTraining')}</Link>
              <Link to="/services/nutrition" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.footerServices.sportsNutrition')}</Link>
              <Link to="/categories/athletic-training" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.footerServices.athleticPerformance')}</Link>
              <Link to="/services/football-training" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.footerServices.footballTraining')}</Link>
              <Link to="/services/vo2-max" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.footerServices.lactateVO2')}</Link>
              <Link to="/services/fat-analysis" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.footerServices.bodyComposition')}</Link>
            </nav>
          </div>

          {/* Follow */}
          <div className="flex flex-col items-center md:items-end">
            <h3 className="text-lg font-bold text-foreground mb-4">{t('footer.followUs')}</h3>
            <div className="flex gap-4">
              <a href="https://www.youtube.com/@Valorform" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors" aria-label="YouTube">
                <Youtube className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/valorform_performance/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.tiktok.com/@valorform_performance" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors" aria-label="TikTok">
                <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </a>
              <a href="https://www.facebook.com/share/19CGkBPSRw/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
            </div>
            <p className="text-xs text-muted-foreground mt-4 text-center md:text-right">
              {t('footer.address.line1')}<br />{t('footer.address.line2')}
            </p>
            <iframe
              title="Valorform location — Ingeborg-Bachmann-Str., Munich"
              src="https://www.openstreetmap.org/export/embed.html?bbox=11.6520%2C48.1300%2C11.7020%2C48.1560&layer=mapnik&marker=48.1430%2C11.6770"
              width="100%"
              height="180"
              style={{ border: 0, marginTop: "0.75rem", borderRadius: "0.5rem" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a
              href="https://www.google.com/maps/search/?api=1&query=Ingeborg-Bachmann-Str.%2C%2081829%20M%C3%BCnchen"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-muted-foreground hover:text-primary transition-colors mt-2 text-center md:text-right"
            >
              {t('footer.address.line1')} — Google Maps
            </a>

          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-border">
          <h3 className="text-lg font-bold text-foreground mb-3 text-center md:text-left">{t('footer.localOffersTitle')}</h3>

          <nav aria-label={t('footer.localOffersAria')} className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2 text-sm">
            <Link to="/leistungsdiagnostik-trudering" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.localOffers.trudering')}</Link>
            <Link to="/vo2max-test-riem" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.localOffers.vo2Riem')}</Link>
            <Link to="/personal-trainer-muenchen-ost" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.localOffers.trainerEast')}</Link>
            <Link to="/personal-trainer-munich" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.localOffers.trainerMunich')}</Link>
            <Link to="/services/nutrition" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.localOffers.nutritionMunich')}</Link>
            <Link to="/categories/athletic-training" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.localOffers.athleticMunich')}</Link>
            <Link to="/services/football-training" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.localOffers.footballMunich')}</Link>
            <Link to="/sitemap" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.localOffers.htmlSitemap', 'Sitemap')}</Link>

          </nav>

          {/* Hidden local SEO text — crawlable by Google, Google Maps and other search engines */}
          <p className="sr-only">
            Valorform is a sports science and personal training studio in Munich East (München Ost),
            serving Trudering, Riem, Haidhausen, Bogenhausen, Berg am Laim, Messestadt Riem,
            Neuperlach, Ramersdorf, Waldperlach, Zamdorf and the wider Munich metropolitan area.
            Our primary services include Personal Training in Munich, Sports Nutrition coaching in
            Munich, Athletic Performance Training in Munich and Football Individual Coaching in
            Munich. We also offer laboratory-grade Performance Diagnostics, VO2max testing, Lactate
            Threshold testing, Body Composition Analysis (fat analysis, InBody / skinfold
            measurement), Performance Assessment and Evaluation, Athletic Strength & Gym Training,
            Full Body Weight training, Periodized Endurance Training and Sport Psychology coaching.
            Athletes, runners, cyclists, triathletes, footballers and fitness enthusiasts searching
            for a Personal Trainer Munich, Personal Trainer München Ost, Leistungsdiagnostik
            München, VO2max Test München, Laktattest München, Ernährungsberatung Sport München,
            Krafttraining München, Fußballtraining München or Sportpsychologie München will find
            individualized, science-based coaching at Valorform. Sessions are available in English
            and German, with online coaching for clients outside the Munich area. Find us on Google,
            Google Maps, Apple Maps and Bing under Valorform Munich.
          </p>
        </div>

        <div className="mt-8 text-center text-sm">
          <Link to="/review" className="text-muted-foreground hover:text-primary underline underline-offset-4 transition-colors">
            {t('footer.reviewCta')}
          </Link>
        </div>

        <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
          {t('footer.copyright', { year: new Date().getFullYear() })}
        </div>

        {/* Crawlable sitemap: mirrors the static #seo-fallback nav so the JS-rendered DOM matches raw HTML (SEO parity). Off-screen (not clip/display:none) so link scanners keep it in the DOM. */}
        <nav aria-label="Sitemap" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/areas-of-expertise">Services &amp; Areas of Expertise</Link></li>
            <li><Link to="/blog">Training &amp; Nutrition Resources</Link></li>
            <li><Link to="/blog/improve-vo2max">How to Improve Your VO2 Max</Link></li>
            <li><Link to="/blog/lactate-threshold-training">Lactate Threshold Training Guide</Link></li>
            <li><Link to="/blog/nutrition-for-endurance">Sports Nutrition for Endurance</Link></li>
            <li><Link to="/view-plans">Coaching Plans &amp; Pricing</Link></li>
            <li><Link to="/reviews">Client Case Studies &amp; Reviews</Link></li>
            <li><Link to="/elite-athletes">Elite Athletes</Link></li>
            <li><Link to="/normal-people">Fitness Enthusiasts</Link></li>
            <li><Link to="/personal-trainer-munich">Personal Trainer Munich</Link></li>
            <li><Link to="/personal-training-muenchen">Personal Training München</Link></li>
            <li><Link to="/sports-nutrition-muenchen">Sports Nutrition München</Link></li>
            <li><Link to="/athletic-training-muenchen">Athletic Training München</Link></li>
            <li><Link to="/football-training-muenchen">Football Training München</Link></li>
            <li><Link to="/categories/normal-people">Fitness Enthusiast Plans</Link></li>
            <li><Link to="/categories/elite-athletes">Elite Athlete Plans</Link></li>
            <li><Link to="/categories/football-training">Football Training Plans</Link></li>
            <li><Link to="/categories/athletic-training">Athletic Training Plans</Link></li>
            <li><Link to="/categories/normal-people#pricing">Fitness Enthusiast Pricing</Link></li>
            <li><Link to="/categories/elite-athletes#pricing">Elite Athlete Pricing</Link></li>
            <li><Link to="/categories/football-training#pricing">Football Training Pricing</Link></li>
            <li><Link to="/categories/athletic-training#pricing">Athletic Training Pricing</Link></li>
            <li><Link to="/services/evaluation">Evaluation</Link></li>
            <li><Link to="/services/fat-analysis">Fat Analysis</Link></li>
            <li><Link to="/services/football-training">Football Training</Link></li>
            <li><Link to="/services/full-body-weight">Full Body Weight Analysis</Link></li>
            <li><Link to="/services/gym-training">Gym Training</Link></li>
            <li><Link to="/services/lactate-test">Lactate Test</Link></li>
            <li><Link to="/services/nutrition">Nutrition</Link></li>
            <li><Link to="/services/performance-diagnostic">Performance Diagnostic</Link></li>
            <li><Link to="/services/psychology">Psychology</Link></li>
            <li><Link to="/services/vo2-max">VO2 Max Test</Link></li>
            <li><Link to="/guides/vo2-max">VO2 Max Guide</Link></li>
            <li><Link to="/tools">Tools &amp; Calculators</Link></li>
            <li><Link to="/tools/protein-calculator">Protein Calculator</Link></li>
            <li><Link to="/tools/hydration-calculator">Hydration Calculator</Link></li>
            <li><Link to="/tools/calorie-macro-calculator">Calorie &amp; Macro Calculator</Link></li>
            <li><Link to="/tools/sprint-speed-calculator">Sprint Speed Calculator</Link></li>
            <li><Link to="/tools/training-load-calculator">Training Load Calculator</Link></li>
            <li><Link to="/tools/vo2-max-calculator">VO2 Max Calculator</Link></li>
            <li><Link to="/tools/heart-rate-zones-calculator">Heart Rate Zone Calculator</Link></li>
            <li><Link to="/tools/one-rep-max-calculator">One Rep Max Calculator</Link></li>
            <li><Link to="/tools/bmi-calculator">BMI Calculator</Link></li>
            <li><Link to="/tools/body-fat-calculator">Body Fat Calculator</Link></li>
            <li><Link to="/cookies">Cookies</Link></li>
            <li><Link to="/imprint">Imprint</Link></li>
            <li><Link to="/terms">Terms and Conditions</Link></li>
            <li><Link to="/login">Login</Link></li>
            <li><a href="https://www.youtube.com/@Valorform" target="_blank" rel="noopener noreferrer">YouTube</a></li>
            <li><a href="https://www.instagram.com/valorform_performance/" target="_blank" rel="noopener noreferrer">Instagram</a></li>
            <li><a href="https://www.tiktok.com/@valorform_performance" target="_blank" rel="noopener noreferrer">TikTok</a></li>
            <li><a href="https://www.facebook.com/share/19CGkBPSRw/" target="_blank" rel="noopener noreferrer">Facebook</a></li>
          </ul>
        </nav>
      </div>
    </footer>
  );
};
